import { createI18n } from 'vue-i18n'
import es from './locales/es.json'
import en from './locales/en.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import pt from './locales/pt.json'
import cmsEn from './locales/cms/en.json'
import cmsFr from './locales/cms/fr.json'
import cmsIt from './locales/cms/it.json'
import cmsPt from './locales/cms/pt.json'

// Mismo set de idiomas que layout-nuxt-two. El idioma se detecta del navegador
// en la primera visita y luego se recuerda en localStorage cuando el usuario lo
// cambia desde el selector del sidebar.
export const LOCALES = [
  { code: 'es', language: 'es-ES', name: 'Español', flag: '🇪🇸' },
  { code: 'en', language: 'en-US', name: 'English', flag: '🇬🇧' },
  { code: 'fr', language: 'fr-FR', name: 'Français', flag: '🇫🇷' },
  { code: 'it', language: 'it-IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', language: 'pt-BR', name: 'Português', flag: '🇧🇷' },
]

export const DEFAULT_LOCALE = 'es'
const STORAGE_KEY = 'i18n_lang'
const SUPPORTED_CODES = LOCALES.map((locale) => locale.code)

function readStoredLocale() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function detectBrowserLocale() {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const candidate of candidates) {
    const code = String(candidate || '').slice(0, 2).toLowerCase()
    if (SUPPORTED_CODES.includes(code)) return code
  }
  return DEFAULT_LOCALE
}

function resolveInitialLocale() {
  const stored = readStoredLocale()
  if (stored && SUPPORTED_CODES.includes(stored)) return stored
  return detectBrowserLocale()
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: resolveInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  missingWarn: false,
  fallbackWarn: false,
  messages: { es, en, fr, it, pt },
})

/** Idioma activo como código corto (es, en, …). Usable fuera de componentes (stores, servicios). */
export function currentLocale() {
  return i18n.global.locale.value
}

/** Tag BCP 47 del idioma activo (es-ES, en-US, …) para Intl / toLocaleString. */
export function currentLanguageTag() {
  return LOCALES.find((locale) => locale.code === currentLocale())?.language ?? 'es-ES'
}

export function setLocale(code) {
  if (!SUPPORTED_CODES.includes(code)) return
  i18n.global.locale.value = code
  document.documentElement.lang = code
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // Sin storage (modo privado): el cambio vale solo para esta sesión.
  }
}

/** Traducción fuera de componentes (stores, router, servicios). */
export const t = (...args) => i18n.global.t(...args)

/**
 * Traduce un valor que viene crudo de la API (estados, tipos) bajo `prefix`; si no hay traducción
 * para ese valor se muestra tal cual, así un estado nuevo del backend nunca queda como clave rota.
 */
export function tEnum(prefix, value) {
  const raw = String(value ?? '')
  const key = `${prefix}.${raw}`
  return raw && i18n.global.te(key) ? i18n.global.t(key) : raw
}

/**
 * Textos editoriales del CMS (títulos, subtítulos y descripciones de lobbies, nombres de carruseles,
 * banners). El CMS hoy solo los tiene cargados en español, así que se traducen acá con
 * `locales/cms/<lang>.json`, cuya clave es el texto original en español tal como lo devuelve el CMS.
 * - Si el operador edita un texto en el CMS, deja de coincidir con la clave y se muestra lo que diga
 *   el CMS (nunca una traducción vieja de otro texto). Para traducirlo, agregar la nueva clave.
 * - Si el CMS empieza a devolver el texto ya traducido (se le manda `locale`), tampoco coincide y se
 *   usa el del CMS tal cual.
 * Nombres de juegos y proveedores no pasan por acá.
 */
const CMS_MESSAGES = { en: cmsEn, fr: cmsFr, it: cmsIt, pt: cmsPt }

export function tCms(text) {
  const raw = String(text ?? '')
  const code = i18n.global.locale.value
  if (!raw || code === DEFAULT_LOCALE) return raw
  return CMS_MESSAGES[code]?.[raw.trim()] ?? raw
}

document.documentElement.lang = currentLocale()
